import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Avatar,
  IconButton,
  Box,
  Button,
  Popover,
  Pagination,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setPlanForDelete, setPlanForEdit, setPlanForShare, setPlanForView } from "../../Slices/planslice";
import {
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  IosShare as IosShareIcon,
  MoreVert as MoreVertIcon,
} from "@mui/icons-material";
import http from "../../utils/http-common";

const PlanTable = ({ planData = [] }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [sharedData, setSharedData] = useState([]);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const cellStyle = {
    fontFamily: "Inter, sans-serif",
    fontSize: "12px",
    fontWeight: 500,
    textAlign: "left",
  };
  const dataStyle = {
    fontFamily: "Inter, sans-serif",
    fontSize: "10px",
    fontWeight: 500,
    textAlign: "left",
  };

  // Fetch shared data
  useEffect(() => {
    const fetchSharedData = async () => {
      try {
        const response = await http.get("/master_service/get_plan/acess_api/data");
        if (response.status === 200) {
          setSharedData(response.data);
        }
      } catch (error) {
        console.error("Error fetching shared data", error);
      }
    };
    fetchSharedData();
  }, []);

  const handleActionOpen = (event, plan) => {
    setSelectedPlan(plan);
    setAnchorEl(event.currentTarget);
  };
  const handleCloseAction = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    if (selectedPlan) {
      dispatch(setPlanForEdit({ id: selectedPlan.id, isEdit: true, table: false }));
      navigate("/home/createplan");
      handleCloseAction();
    }
  };

  const handleDelete = () => {
    if (selectedPlan) {
      dispatch(setPlanForDelete({ id: selectedPlan.id, isDelete: true, table: false }));
      navigate("/home/createplan");
      handleCloseAction();
    }
  };

  const handleView = () => {
    if (selectedPlan) {
      dispatch(setPlanForView({ id: selectedPlan.id, isView: true, table: false }));
      navigate("/home/viewplan");
      handleCloseAction();
    }
  };

  const handleShare = () => {
    if (selectedPlan) {
      dispatch(setPlanForShare({ id: selectedPlan.id, isShare: true, table: false }));
      navigate("/home/plan-share-screen");
      handleCloseAction();
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  };

  const SharedWithComponent = ({ sharedWithUsers, plan_id, user_id }) => {
    const maxVisibleUsers = 3;
    const relevantData = (sharedWithUsers || []).filter(
      (plan) => plan.org_user_id === user_id && plan.plan_id === plan_id
    );
    
    return (
      <Box display="flex" alignItems="center">
        {relevantData.slice(0, maxVisibleUsers).map((user, index) => (
          <Avatar
            key={index}
            alt={`Profile of ${user.shared_by_username}`}
            src={user.profile}
            sx={{
              width: 20,
              height: 20,
              marginLeft: index > 0 ? -1 : 0,
              border: "2px solid white",
              zIndex: maxVisibleUsers - index,
            }}
          />
        ))}
        {relevantData.length > maxVisibleUsers && (
          <Typography sx={{ marginLeft: 1, fontSize: "14px", fontWeight: 500 }}>
            +{relevantData.length - maxVisibleUsers}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <>
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 150 }} aria-label="user table">
          <TableHead sx={{ backgroundColor: "#e0eaff", height: "50px" }}>
            <TableRow>
              <TableCell sx={cellStyle}>Project Name</TableCell>
              <TableCell sx={cellStyle}>Customer Name</TableCell>
              <TableCell sx={cellStyle}>Created By</TableCell>
              <TableCell sx={cellStyle}>Created At</TableCell>
              <TableCell sx={cellStyle}>Shared With</TableCell>
              <TableCell sx={cellStyle}>Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody sx={{ height: "10px" }}>
            {planData.map((plan) => (
              <TableRow key={plan.id}>
                <TableCell sx={dataStyle}>{plan.plan_name}</TableCell>
                <TableCell sx={dataStyle}>{plan.customer_name}</TableCell>
                <TableCell sx={dataStyle}>{plan.created_by}</TableCell>
                <TableCell sx={dataStyle}>{formatDate(plan.createdAt)}</TableCell>
                <TableCell>
                  <SharedWithComponent plan_id={plan.plan_id} user_id={plan.user_id} sharedWithUsers={sharedData} />
                </TableCell>
                <TableCell>
                  <IconButton onClick={(event) => handleActionOpen(event, plan)}>
                    <MoreVertIcon sx={{ height: "20px", width: "20px" }} />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box display="flex" justifyContent="center" sx={{ mt: 2 }}>
        <Pagination count={10} color="primary" />
      </Box>

      <Popover
        id="user-options-popover"
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleCloseAction}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Box sx={{ p: 1, width: "130px" }}>
          <Button onClick={handleView} fullWidth sx={{ justifyContent: "flex-start", color: "black" }}>
            <VisibilityIcon fontSize="small" sx={{ mr: 1 }} />
            <Typography variant="body1" sx={{ fontSize: "12px", fontWeight: 500 }}>View Plan</Typography>
          </Button>
          {selectedPlan?.created_by === localStorage.getItem("user_name") && (
            <>
              <Button onClick={handleEdit} fullWidth sx={{ justifyContent: "flex-start", color: "black" }}>
                <EditIcon fontSize="small" sx={{ mr: 1 }} />
                <Typography variant="body1" sx={{ fontSize: "12px", fontWeight: 500 }}>Edit Plan</Typography>
              </Button>
              <Button onClick={handleShare} fullWidth sx={{ justifyContent: "flex-start", color: "black" }}>
                <IosShareIcon fontSize="small" sx={{ mr: 1 }} />
                <Typography variant="body1" sx={{ fontSize: "12px", fontWeight: 500 }}>Share Plan</Typography>
              </Button>
              <Button onClick={handleDelete} fullWidth sx={{ justifyContent: "flex-start", color: "black" }}>
                <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                <Typography variant="body1" sx={{ fontSize: "12px", fontWeight: 500 }}>Delete Plan</Typography>
              </Button>
            </>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default PlanTable;
